// =========================================================================
// SettingsSearch
//
// A dumb-simple Cmd+K-style command palette scoped to /settings/*. The
// index is hand-authored in `searchIndex.ts`; we substring-match across
// label / section / tab / hint / keywords with a tiny ranking pass and
// render at most 12 results. Picking a result navigates to
// `<path>#<hash>` and `AnchorWatcher` (mounted once in `SettingsLayout`)
// scrolls to the matching DOM id and flashes a brief ring.
//
// Admin-only entries (e.g. Store templates, Billing) are filtered out
// for non-admins via `useMyOrgRole`, so the dropdown never advertises
// settings the user can't reach.
// =========================================================================

import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Input } from '@pc/ui';
import {
  SETTINGS_SEARCH_ENTRIES,
  matchSettingsSearch,
  type SettingsSearchEntry,
} from './searchIndex';
import { isAdminRole, useMyOrgRole } from './useMyOrgRole';

export function SettingsSearch() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const roleQ = useMyOrgRole();
  const isAdmin = isAdminRole(roleQ.data);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const listboxId = useId();

  const matches = useMemo(
    () =>
      matchSettingsSearch(query, SETTINGS_SEARCH_ENTRIES, {
        isAdmin,
        max: 12,
      }),
    [query, isAdmin],
  );

  // Cmd/Ctrl+K -> focus the input. The shortcut is global to /settings/*
  // because the layout is the only mount point; if a user is on another
  // page the listener simply doesn't exist.
  useEffect(() => {
    function handler(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        inputRef.current?.focus();
        inputRef.current?.select();
      }
    }
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  // Close the dropdown when clicking outside the wrapper.
  useEffect(() => {
    function handler(e: MouseEvent) {
      if (!containerRef.current) return;
      if (!containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  useEffect(() => {
    setActiveIndex(0);
  }, [query]);

  function pick(entry: SettingsSearchEntry) {
    const url = entry.hash ? `${entry.path}#${entry.hash}` : entry.path;
    navigate(url);
    setOpen(false);
    setQuery('');
    inputRef.current?.blur();
  }

  function onKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Escape') {
      setOpen(false);
      inputRef.current?.blur();
      return;
    }
    if (!matches.length) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex((i) => Math.min(matches.length - 1, i + 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((i) => Math.max(0, i - 1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const entry = matches[activeIndex];
      if (entry) pick(entry);
    }
  }

  const showDropdown = open && query.trim().length > 0;

  return (
    <div
      ref={containerRef}
      className="relative w-full max-w-sm"
      role="search"
    >
      <Input
        ref={inputRef}
        type="search"
        value={query}
        onChange={(e) => {
          setQuery(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onKeyDown={onKeyDown}
        placeholder={t('settings.search.placeholder', 'Search settings…')}
        aria-label={t('settings.search.ariaLabel', 'Search settings')}
        aria-controls={showDropdown ? listboxId : undefined}
        aria-expanded={showDropdown}
        aria-autocomplete="list"
        autoComplete="off"
        className="pr-12"
      />
      <kbd
        aria-hidden
        className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 hidden sm:inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] tabular border border-rule text-muted bg-paper"
      >
        ⌘K
      </kbd>
      {showDropdown ? (
        <ul
          id={listboxId}
          role="listbox"
          className="absolute left-0 right-0 z-30 mt-1 max-h-80 overflow-auto border border-rule-strong bg-paper shadow-sm"
        >
          {matches.length === 0 ? (
            <li className="px-3 py-2 text-xs text-muted">
              {t('settings.search.empty', 'No matching settings.')}
            </li>
          ) : (
            matches.map((m, i) => (
              <li key={m.id} role="option" aria-selected={i === activeIndex}>
                <button
                  type="button"
                  onMouseDown={(e) => {
                    e.preventDefault();
                    pick(m);
                  }}
                  onMouseEnter={() => setActiveIndex(i)}
                  className={`flex w-full items-baseline justify-between gap-3 px-3 py-2 text-left ${
                    i === activeIndex ? 'bg-linen' : 'hover:bg-paper-2'
                  }`}
                >
                  <span className="flex flex-col min-w-0">
                    <span className="text-sm text-ink truncate">{m.label}</span>
                    {m.hint ? (
                      <span className="text-xs text-muted truncate">{m.hint}</span>
                    ) : null}
                  </span>
                  <span className="shrink-0 text-[10px] tabular text-muted uppercase tracking-wide">
                    {m.tab}
                    {m.section && m.section !== m.tab ? ` · ${m.section}` : ''}
                  </span>
                </button>
              </li>
            ))
          )}
        </ul>
      ) : null}
    </div>
  );
}
